import React, { useEffect, useMemo, useState } from "react";
import { IoArrowUp, IoArrowDown, IoArrowForward } from "react-icons/io5";
const MANIFEST_URL =
  "https://00117-starek.s3.us-east-1.amazonaws.com/firmwares/firmware_manifest.json";

export default function UpdateFirmwareModal({
  open,
  onClose,
  model,
  currentVersion,
  currentCode,
  onConfirm,
  title = "Update Firmware",
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [list, setList] = useState([]);

  useEffect(() => {
    if (!open || !model) return;

    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError("");
      setList([]);

      try {
        const res = await fetch(MANIFEST_URL, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        if (!res.ok) throw new Error(`Manifest fetch failed (${res.status})`);

        const manifest = await res.json();
        const items = Array.isArray(manifest?.[model]) ? manifest[model] : [];

        const sorted = [...items].sort((a, b) => {
          const da = new Date(a.release_date || "1970-01-01").getTime();
          const db = new Date(b.release_date || "1970-01-01").getTime();
          if (db !== da) return db - da;
          return (b.firmware_code || 0) - (a.firmware_code || 0);
        });

        if (!cancelled) setList(sorted.slice(0, 10));
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to load firmware list.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [open, model]);

  const currentEntry = useMemo(() => {
    if (!Array.isArray(list) || list.length === 0) return null;
    const byCode =
      Number.isFinite(Number(currentCode)) &&
      list.find((x) => Number(x.firmware_code) === Number(currentCode));
    if (byCode) return byCode;
    if (currentVersion) {
      return list.find((x) => x.firmware_version === currentVersion) || null;
    }
    return null;
  }, [list, currentCode, currentVersion]);

  const currentReleaseMs = useMemo(() => {
    return currentEntry
      ? new Date(currentEntry.release_date || "1970-01-01").getTime()
      : null;
  }, [currentEntry]);

  const latestVersion = useMemo(
    () => (list.length > 0 ? list[0].firmware_version : ""),
    [list]
  );

  if (!open) return null;

  return (
    <div className="tw-fixed tw-inset-0 tw-z-[1000] tw-flex tw-items-center tw-justify-center tw-bg-black/40">
      <div className="tw-bg-white tw-rounded-2xl tw-shadow-xl tw-w-[92vw] md:tw-w-[520px] tw-overflow-hidden">
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-4 tw-border-b">
          <h3 className="tw-text-xl tw-font-bold">{title}</h3>
          <button
            onClick={onClose}
            className="tw-text-2xl tw-leading-none tw-text-gray-500 hover:tw-text-gray-700"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <div className="tw-px-5 tw-pt-3 tw-pb-4 tw-max-h-[65vh] tw-overflow-y-auto">
          <div className="tw-text-sm tw-text-gray-500 tw-mb-3">
            Model: <span className="tw-font-medium">{model || "—"}</span>
          </div>

          {loading && <div className="tw-py-6">Loading versions…</div>}
          {error && <div className="tw-text-red-600 tw-py-4">{error}</div>}

          {!loading && !error && list.length === 0 && (
            <div className="tw-py-6 tw-text-sm tw-text-gray-500">
              No firmware entries found for this model.
            </div>
          )}

          {!loading &&
            !error &&
            list.map((fw, idx) => {
              const isLatest = idx === 0;
              const isCurrent =
                (Number.isFinite(Number(currentCode)) &&
                  Number(fw.firmware_code) === Number(currentCode)) ||
                fw.firmware_version === currentVersion;

              const relMs = new Date(fw.release_date || "1970-01-01").getTime();
              const isNewerVsCurrent =
                currentReleaseMs != null ? relMs > currentReleaseMs : isLatest;
              const isOlderVsCurrent =
                currentReleaseMs != null ? relMs < currentReleaseMs : !isLatest;

              const Icon = isCurrent
                ? IoArrowForward
                : isNewerVsCurrent
                ? IoArrowUp
                : IoArrowDown;
              const iconClass = isCurrent
                ? "tw-text-gray-400"
                : isNewerVsCurrent
                ? "tw-text-green-600"
                : "tw-text-red-500";

              return (
                <button
                  key={`${fw.firmware_code}-${fw.firmware_version}`}
                  className="tw-w-full tw-text-left tw-py-3 tw-border-b last:tw-border-b-0 hover:tw-bg-gray-50 tw-flex tw-items-center tw-justify-between"
                  onClick={() => onConfirm?.(fw)}
                >
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <Icon className={iconClass} size={18} />
                    <div className="tw-flex tw-flex-col">
                      <span className="tw-text-sm tw-text-gray-900">
                        {fw.firmware_version}
                      </span>
                      <span className="tw-text-xs tw-text-gray-500">
                        Code: {fw.firmware_code} · Released{" "}
                        {fw.release_date || "—"}
                      </span>
                    </div>
                  </div>

                  <div className="tw-flex tw-gap-2">
                    {isLatest && (
                      <span className="tw-text-xs tw-text-blue-600">
                        (latest)
                      </span>
                    )}
                    {isCurrent && (
                      <span className="tw-text-xs tw-text-blue-600">
                        (Current)
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
        </div>
      </div>
    </div>
  );
}
